import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';

/**
 * Composable that provides knowledge base document filtering context
 * @returns {{ filterTypes: import('vue').ComputedRef }}
 */
export function useKnowledgeBaseDocumentFilterContext() {
  const { t } = useI18n();

  const {
    equalityOperators,
    containmentOperators,
    dateOperators,
    comparisonOperators,
  } = useOperators();

  /**
   * @type {import('vue').ComputedRef}
   */
  const filterTypes = computed(() => [
    {
      attributeKey: 'name',
      value: 'name',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_NAME'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_NAME'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'status',
      value: 'status',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_STATUS'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_STATUS'),
      inputType: 'searchSelect',
      options: [
        {
          id: 'processing',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_PROCESSING'),
        },
        {
          id: 'crawling',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_CRAWLING'),
        },
        {
          id: 'chunking',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_CHUNKING'),
        },
        {
          id: 'embedding',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_EMBEDDING'),
        },
        {
          id: 'available',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_AVAILABLE'),
        },
        {
          id: 'crawl_failed',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_CRAWL_FAILED'),
        },
        {
          id: 'chunk_failed',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_CHUNK_FAILED'),
        },
        {
          id: 'embedding_failed',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_EMBEDDING_FAILED'),
        },
      ],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'file_type',
      value: 'file_type',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CONTENT_TYPE'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CONTENT_TYPE'),
      inputType: 'searchSelect',
      options: [
        {
          id: 'pdf',
          name: 'PDF',
        },
        {
          id: 'txt',
          name: 'TXT',
        },
        {
          id: 'md',
          name: 'Markdown',
        },
        {
          id: 'docx',
          name: 'DOCX',
        },
        {
          id: 'pptx',
          name: 'PPTX',
        },
        {
          id: 'url',
          name: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CONTENT_TYPE_LINK'),
        },
      ],
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'file_size',
      value: 'file_size',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_FILE_SIZE'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_FILE_SIZE'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'chunks_count',
      value: 'chunks_count',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CHUNKS_COUNT'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CHUNKS_COUNT'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'character_count',
      value: 'character_count',
      attributeName: t(
        'PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CHARACTER_COUNT'
      ),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CHARACTER_COUNT'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'created_at',
      value: 'created_at',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CREATED_AT'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_CREATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: 'updated_at',
      value: 'updated_at',
      attributeName: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_UPDATED_AT'),
      label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FILTER_UPDATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
  ]);

  return { filterTypes };
}
